/**
 * dom.js — small DOM/format helpers plus the JSON API client.
 * Kept dependency-free so it can be imported from anywhere without cycles.
 */

import {getLang, toLatinDigits, gettext} from './i18n.js';

/* ------------------------------------------------------------------ *
 * Escaping and text
 * ------------------------------------------------------------------ */

const ENTITIES = {
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};

/** Escape a value for interpolation into an HTML template literal. */
export function esc(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[&<>"']/g, (c) => ENTITIES[c]);
}

/** Escape for use inside an HTML attribute that will be quoted with ". */
export const escAttr = esc;

/** Strip tags and clamp to a word count, for card summaries. */
export function truncate(text, words = 24) {
    if (!text) return '';
    const clean = String(text).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const parts = clean.split(' ');
    if (parts.length <= words) return clean;
    return `${parts.slice(0, words).join(' ')}…`;
}

/** Inline placeholder used when a media file is missing, as a data URI so a
 *  broken path can never trigger a second request and loop. */
export const PLACEHOLDER_IMG =
    "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23eef2f6'/%3E%3Cpath d='M250 110h-24.6l-10.4-18h-30l-10.4 18H150c-11 0-20 9-20 20v80c0 11 9 20 20 20h100c11 0 20-9 20-20v-80c0-11-9-20-20-20zm-50 90c-19.3 0-35-15.7-35-35s15.7-35 35-35 35 15.7 35 35-15.7 35-35 35z' fill='%23c2ccd6'/%3E%3C/svg%3E";

/* ------------------------------------------------------------------ *
 * Element helpers
 * ------------------------------------------------------------------ */

export const qs = (selector, root = document) => root.querySelector(selector);
export const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

/** Build an element from an HTML string. */
export function fromHTML(html) {
    const template = document.createElement('template');
    template.innerHTML = String(html).trim();
    return template.content.firstElementChild;
}

/** Delegated event binding — survives re-renders of the container. */
export function on(root, eventName, selector, handler) {
    root.addEventListener(eventName, (event) => {
        const target = event.target.closest(selector);
        if (target && root.contains(target)) handler(event, target);
    });
}

/** Toggle body scroll lock without losing the scroll position. */
let lockedScrollY = 0;

export function lockScroll(locked) {
    if (locked) {
        lockedScrollY = window.scrollY;
        document.body.classList.add('nv-scroll-locked');
        document.body.style.top = `-${lockedScrollY}px`;
        document.body.style.position = 'fixed';
        document.body.style.insetInline = '0';
    } else {
        document.body.classList.remove('nv-scroll-locked');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.insetInline = '';
        window.scrollTo(0, lockedScrollY);
    }
}

/* ------------------------------------------------------------------ *
 * API client
 * ------------------------------------------------------------------ */

function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
}

export function csrfToken() {
    return getCookie('csrftoken') || (window.NOVA_BOOT || {}).csrfToken || '';
}

export class ApiError extends Error {
    constructor(message, {status = 0, payload = null} = {}) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.payload = payload;
    }
}

/**
 * GET a JSON endpoint with the active language attached.
 * Any user-supplied param is normalised to ASCII digits first, so a query
 * typed on a Persian keyboard still matches server-side.
 */
export async function apiGet(path, params = {}) {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('lang', getLang());

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        url.searchParams.set(key, toLatinDigits(value));
    });

    let response;
    try {
        response = await fetch(url.toString(), {
            headers: {Accept: 'application/json', 'X-Requested-With': 'fetch'},
            credentials: 'same-origin',
        });
    } catch (err) {
        throw new ApiError(err.message, {status: 0});
    }

    if (!response.ok) {
        throw new ApiError(`${gettext('Request failed:')} ${response.status}`, {status: response.status});
    }

    return response.json();
}

/** POST JSON with CSRF. Numeric values are normalised to ASCII digits. */
export async function apiPost(path, body = {}) {
    const payload = {};
    Object.entries(body).forEach(([key, value]) => {
        payload[key] = typeof value === 'string' ? toLatinDigits(value) : value;
    });

    let response;
    try {
        response = await fetch(path, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken(),
                'X-Requested-With': 'fetch',
            },
            credentials: 'same-origin',
            body: JSON.stringify(payload),
        });
    } catch (err) {
        throw new ApiError(err.message, {status: 0});
    }

    let data = null;
    try {
        data = await response.json();
    } catch { /* empty body is fine */
    }

    if (!response.ok) {
        throw new ApiError(
            (data && (data.error || data.detail)) || `${gettext('Request failed:')} ${response.status}`,
            {status: response.status, payload: data}
        );
    }

    return data;
}

/* ------------------------------------------------------------------ *
 * Render helpers
 * ------------------------------------------------------------------ */

/** Image tag with placeholder fallback wired to onerror exactly once. */
export function img(src, alt = '', className = '') {
    const safeSrc = src ? escAttr(src) : PLACEHOLDER_IMG;
    return `<img src="${safeSrc}" alt="${escAttr(alt)}"${className ? ` class="${escAttr(className)}"` : ''} loading="lazy" decoding="async" onerror="this.onerror=null;this.src='${PLACEHOLDER_IMG}'">`;
}

/** Icon markup. Everything is FontAwesome; no emoji anywhere in the UI. */
export function icon(name, {solid = true, className = '', fixed = false} = {}) {
    const style = solid ? 'fa-solid' : 'fa-regular';
    const classes = [style, `fa-${name}`, fixed ? 'fa-fw' : '', className]
        .filter(Boolean).join(' ');
    return `<i class="${classes}" aria-hidden="true"></i>`;
}

/** Render an empty state block. */
export function emptyState({iconName = 'inbox', title = '', text = '', action = ''}) {
    return `
    <div class="nv-empty">
      <div class="nv-empty__icon">${icon(iconName)}</div>
      ${title ? `<p class="nv-empty__title">${esc(title)}</p>` : ''}
      ${text ? `<p class="nv-empty__text">${esc(text)}</p>` : ''}
      ${action || ''}
    </div>`;
}
